import {HistoryIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, RelativeTime} from '@primer/react'
import {useState} from 'react'

import {useScribeArtifact} from '../hooks'
import type {ScribeArtifactResourceId, ScribeArtifactResourceType, ScribeArtifactVersion} from '../types'
import styles from './ArtifactHistoryMenu.module.css'

type ArtifactHistoryMenuProps = {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
  number: number
  selectedVersion: ScribeArtifactVersion | null
  onSelect: (version: ScribeArtifactVersion) => void
}

export function ArtifactHistoryMenu({
  resourceType,
  resourceId,
  number,
  selectedVersion,
  onSelect,
}: ArtifactHistoryMenuProps) {
  const [open, setOpen] = useState(false)

  return (
    <ActionMenu open={open} onOpenChange={setOpen}>
      <ActionMenu.Button variant="invisible" size="small" className={styles.HistoryButton} leadingVisual={HistoryIcon}>
        Edit history
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium">
        {open && (
          <ArtifactHistoryMenuContent
            resourceType={resourceType}
            resourceId={resourceId}
            number={number}
            selectedVersion={selectedVersion}
            onSelect={onSelect}
          />
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function ArtifactHistoryMenuContent({
  resourceType,
  resourceId,
  number,
  selectedVersion,
  onSelect,
}: ArtifactHistoryMenuProps) {
  const {data, isError, isPending} = useScribeArtifact({resourceType, resourceId, number})
  const versions = data?.versions ?? []
  const editCount = Math.max(versions.length - 1, 0)
  const statusMessage = isPending
    ? 'Loading edit history…'
    : isError
      ? 'Unable to load edit history'
      : versions.length === 0
        ? 'No version history available'
        : null

  return (
    <ActionList className={styles.HistoryList} selectionVariant={statusMessage ? undefined : 'single'}>
      <ActionList.Group>
        {!statusMessage && (
          <ActionList.GroupHeading>{`${versions.length} version${versions.length === 1 ? '' : 's'} · ${editCount} edit${editCount === 1 ? '' : 's'}`}</ActionList.GroupHeading>
        )}
        {statusMessage ? (
          <ActionList.Item key="initial-focus-target" disabled>
            {statusMessage}
          </ActionList.Item>
        ) : (
          versions.map((version, index) => {
            const isCurrent = index === 0
            const isOriginal = index === versions.length - 1
            const versionLabel = isCurrent
              ? 'Current version'
              : isOriginal
                ? 'Original version'
                : `Version ${version.version}`

            return (
              <ActionList.Item
                key={isCurrent ? 'initial-focus-target' : version.version}
                selected={selectedVersion?.version === version.version || (!selectedVersion && isCurrent)}
                onSelect={() => onSelect(version)}
              >
                <div className={styles.HistoryVersionContent}>
                  <span className={styles.HistoryVersionLabel}>{versionLabel}</span>
                  <span className={styles.HistoryVersionMetadata}>
                    <span>{`${isOriginal ? 'Created' : 'Edited'} by ${version.actor?.login ?? 'Unknown user'}`}</span>
                    {version.created_at && (
                      <>
                        <span aria-hidden="true">·</span>
                        <RelativeTime date={new Date(version.created_at)} />
                      </>
                    )}
                  </span>
                </div>
              </ActionList.Item>
            )
          })
        )}
      </ActionList.Group>
    </ActionList>
  )
}
